/** VISTOERA_CANVAS_CANONICAL_HOVER_QUICK_ACTION_ADMIN_DRIVEN_NEXT_NODE_
 * CONTEXT_HANDOFF_P0_V1 - tenant/session Quick Action cache.
 *
 * ONE shared config state for every Canvas node (spec Gate L -
 * `PER_NODE_EXTRA_FETCH=0`, `HOVER_EXTRA_FETCH=0`): fetched via the
 * workspace-scoped read endpoint
 * (`GET /workspaces/{id}/node-metadata/quick-actions`), which already
 * returns every ENABLED action across every source node type in one
 * shot - never per-source-type, never per-node, never on hover.
 *
 * TASK-00584 hardening:
 *  - the snapshot is scoped by `${userId}::${workspaceId}` (same key
 *    shape as the node catalog cache); switching account / workspace
 *    drops it immediately so nothing leaks across accounts/workspaces;
 *  - `refreshWorkspaceQuickActions` re-fetches on every Canvas entry
 *    (stale-while-revalidate for the SAME key) and is de-duplicated
 *    while in flight, so an admin toggle takes effect on re-entering /
 *    refreshing the page - no polling, no websocket;
 *  - fail-closed everywhere: before the first successful fetch, and
 *    after a failed fetch with no prior snapshot, NO action renders and
 *    NO action may execute. A failed fetch is never recorded as
 *    "loaded", so the next entry retries.
 *
 * `getEnabledQuickActionsForSource` is the only thing render code needs
 * - already ordered by `sort_order` so a `CanvasResultQuickActionRail`
 * consumer never re-sorts itself. */
import type { QuickActionAdminRow } from '../../../api'
import { getWorkspaceQuickActions } from '../../../api'
import {
  groupQuickActionsBySource as groupQuickActionRowsBySource,
  isQuickActionEnabledInRows,
} from './canvasQuickActionResolution'

let _actionsBySourceType: Map<string, QuickActionAdminRow[]> = new Map()
/** `${userId}::${workspaceId}` the current snapshot belongs to; set ONLY after a successful fetch. */
let _loadedForKey: string | null = null
let _loadedAt = 0
let _requestSeq = 0
let _inFlight: { key: string; promise: Promise<void>; token: number } | null = null
let _revision = 0
const _listeners = new Set<() => void>()

/** Visibility-triggered refresh is skipped while the snapshot is younger than this. */
export const QUICK_ACTION_CONFIG_MIN_REFRESH_MS = 2 * 60 * 1000

function _notify() {
  _revision += 1
  for (const l of _listeners) l()
}

function cacheKey(workspaceId: string, userId?: string | null): string {
  return `${userId || ''}::${workspaceId}`
}

export function getQuickActionConfigRevision(): number {
  return _revision
}

export function subscribeQuickActionConfig(listener: () => void): () => void {
  _listeners.add(listener)
  return () => {
    _listeners.delete(listener)
  }
}

export function isQuickActionConfigLoaded(): boolean {
  return _loadedForKey !== null
}

/** Enabled actions for one source node type, already `sort_order`-sorted.
 * Before the first successful fetch resolves this returns an empty list
 * (fail-closed - the hover rail renders nothing rather than an
 * unvalidated guess, so there is no "all actions" flicker). */
export function getEnabledQuickActionsForSource(sourceNodeType: string): QuickActionAdminRow[] {
  return _actionsBySourceType.get(sourceNodeType) ?? []
}

/** Execution guard - true only when the Admin config snapshot currently
 * contains `source -> target` as an enabled action. */
export function isQuickActionEnabled(sourceNodeType: string, targetNodeType: string): boolean {
  return isQuickActionEnabledInRows(getEnabledQuickActionsForSource(sourceNodeType), targetNodeType)
}

/** Re-exported for existing callers - the actual grouping/sort math now
 * lives in `canvasQuickActionResolution.ts` (zero `api.ts` runtime
 * dependency, so it stays safely importable from a plain node-test). */
export const groupQuickActionsBySource = groupQuickActionRowsBySource

function _applyActions(actions: QuickActionAdminRow[]): void {
  _actionsBySourceType = groupQuickActionsBySource(actions)
  _notify()
}

function _clearSnapshot(): void {
  _actionsBySourceType = new Map()
  _loadedForKey = null
  _loadedAt = 0
  _notify()
}

/** Account switch / logout: never reuse another account's snapshot. */
export function invalidateQuickActionCache(): void {
  _requestSeq += 1
  _inFlight = null
  _clearSnapshot()
}

/** Fetch (or re-fetch) the Admin-enabled Quick Actions for this user+workspace.
 * Same-key callers keep seeing the previous snapshot until the new one
 * lands (no flicker); a different account/workspace never sees another
 * key's snapshot. Concurrent calls share one request. */
export function refreshWorkspaceQuickActions(workspaceId: string, userId?: string | null): Promise<void> {
  if (!workspaceId) return Promise.resolve()
  const key = cacheKey(workspaceId, userId)
  if (_loadedForKey !== null && _loadedForKey !== key) _clearSnapshot()
  if (_inFlight && _inFlight.key === key) return _inFlight.promise
  const token = ++_requestSeq
  const promise = (async () => {
    try {
      const res = await getWorkspaceQuickActions(workspaceId)
      // A newer request (another key) or an invalidation superseded this one.
      if (token !== _requestSeq) return
      _loadedForKey = key
      _loadedAt = Date.now()
      _applyActions(res.data.actions ?? [])
    } catch {
      // Fail-closed: keep only a previous snapshot of THIS key (the last
      // known Admin truth); with none, nothing renders/executes. Never
      // marked as loaded, so the next entry retries.
    } finally {
      if (_inFlight && _inFlight.token === token) _inFlight = null
    }
  })()
  _inFlight = { key, promise, token }
  return promise
}

/** Back-compat entry: load once if this key has no snapshot yet. */
export async function ensureWorkspaceQuickActionsLoaded(workspaceId: string, userId?: string | null): Promise<void> {
  if (!workspaceId) return
  if (_loadedForKey === cacheKey(workspaceId, userId)) return
  await refreshWorkspaceQuickActions(workspaceId, userId)
}

/** Lightweight invalidation for a long-lived Canvas tab: refresh on tab
 * re-focus only when the snapshot is older than the minimum interval. */
export function refreshWorkspaceQuickActionsIfStale(workspaceId: string, userId?: string | null): Promise<void> {
  if (!workspaceId) return Promise.resolve()
  if (_loadedForKey === cacheKey(workspaceId, userId) && Date.now() - _loadedAt < QUICK_ACTION_CONFIG_MIN_REFRESH_MS) {
    return Promise.resolve()
  }
  return refreshWorkspaceQuickActions(workspaceId, userId)
}

/** Test-only synchronous seed - bypasses the network fetch entirely so
 * unit tests can assert render/filter behavior against a fixed,
 * synthetic Admin config without a running backend. */
export function applyQuickActionsForTests(actions: QuickActionAdminRow[], workspaceId = 'test-workspace', userId = 'test-user'): void {
  _loadedForKey = cacheKey(workspaceId, userId)
  _loadedAt = Date.now()
  _applyActions(actions)
}

export function resetQuickActionCacheForTests(): void {
  invalidateQuickActionCache()
}
